import { AutoFocusType } from 'components/booking-modal/BookingProfileFormTab';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { core, safeToNumber, toJSON, toJSONArray } from '@tock/coretypes';
import { dashboard } from 'types';
import { find } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

interface State {
  editing: boolean;
  autoFocus?: AutoFocusType;
  error?: string;
  purchaseRequestInProgress: boolean;
  purchase?: core.PastPurchase;
  ticketGroup?: core.TicketGroup;
  timelineEvents: core.PurchaseTimelineEvent[];
  timelineEventsInitialized: boolean;
  visit?: core.Visit;
  checks: dashboard.PosCheck[];
}

const initialState: State = {
  editing: false,
  autoFocus: undefined,
  error: undefined,
  purchaseRequestInProgress: false,
  purchase: undefined,
  ticketGroup: undefined,
  timelineEvents: [],
  timelineEventsInitialized: false,
  visit: undefined,
  checks: [],
};

export default function reducer(state = initialState, action: BaseTockAction): State {
  const { type, payload, context } = action;
  switch (type) {
    case SharedAppConstants.BOOKING_MODAL_CLEAR:
      return {
        ...state,
        purchase: undefined,
        ticketGroup: undefined,
        timelineEvents: [],
        timelineEventsInitialized: false,
        checks: [],
      };
    case SharedAppConstants.BOOKING_MODAL_SET_EDITING:
      return {
        ...state,
        editing: payload.editing,
        autoFocus: payload.autoFocus,
      };
    case SharedAppConstants.PURCHASE_GET_STARTING:
    case SharedAppConstants.QUESTION_RESENT_STARTING:
      return {
        ...state,
        purchaseRequestInProgress: true,
      };
    case SharedAppConstants.PURCHASE_GET_FAILED:
    case SharedAppConstants.QUESTION_RESENT_FAILED:
      return {
        ...state,
        purchaseRequestInProgress: false,
      };
    case SharedAppConstants.QUESTION_RESPONSE_UPDATE_DONE:
    case SharedAppConstants.QUESTION_RESENT_DONE:
    case SharedAppConstants.PURCHASE_GET_DONE: {
      return {
        ...state,
        purchase: toJSON<core.PastPurchase>(payload),
        purchaseRequestInProgress: false,
      };
    }
    case SharedAppConstants.TICKET_GROUP_GET_DONE:
      return {
        ...state,
        ticketGroup: payload,
        purchaseRequestInProgress: false,
      };
    case SharedAppConstants.TICKET_GROUP_GET_STARTING:
      return {
        ...state,
        error: undefined,
        purchaseRequestInProgress: true,
        ticketGroup: undefined,
      };
    case SharedAppConstants.TICKET_GROUP_GET_FAILED:
      return {
        ...state,
        error: 'Unable to get latest data',
        purchaseRequestInProgress: false,
      };
    case SharedAppConstants.PURCHASE_FETCH_TIMELINE_EVENTS_DONE: {
      return {
        ...state,
        timelineEvents: payload,
        timelineEventsInitialized: true,
      };
    }
    case SharedAppConstants.PURCHASE_FETCH_TIMELINE_EVENTS_STARTING:
      return {
        ...state,
        error: undefined,
        timelineEventsInitialized: false,
      };
    case SharedAppConstants.PURCHASE_FETCH_TIMELINE_EVENTS_FAILED:
      return {
        ...state,
        error: 'Unable to get latest data',
        timelineEventsInitialized: true,
      };
    case SharedAppConstants.VISIT_UPDATE_DONE: {
      // The visit we use in the client can come from three places
      // The id of this place is passed in as context, updating it
      // saves us from having to make another api request to refresh the
      // whole ticket group.

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newState: any = {
        ...state,
      };
      newState.purchaseRequestInProgress = false;
      if ((oc(context).purchaseId() || payload.purchaseId) && newState.purchase) {
        newState.purchase.visit = payload;
      } else if ((oc(context).ticketSubsetId() || payload.ticketSubsetId) && newState.ticketGroup) {
        const subset = find(
          newState.ticketGroup.ticketSubset,
          (subset) =>
            safeToNumber(subset.id) === (oc(context).ticketSubsetId() || payload.ticketSubsetId)
        );
        if (subset) {
          subset.visit = payload;
        }
      } else {
        newState.visit = payload;
      }
      return newState;
    }
    case SharedAppConstants.VISIT_POS_CHECK_GET_STARTING: {
      return {
        ...state,
        checks: [],
        purchaseRequestInProgress: true,
      };
    }
    case SharedAppConstants.VISIT_POS_CHECK_GET_FAILED: {
      return {
        ...state,
        checks: [],
        purchaseRequestInProgress: false,
      };
    }
    case SharedAppConstants.VISIT_POS_CHECK_GET_DONE: {
      return {
        ...state,
        checks: toJSONArray(payload),
        purchaseRequestInProgress: false,
      };
    }
    case SharedAppConstants.VISIT_GET_DONE:
      return {
        ...state,
        visit: payload,
        purchaseRequestInProgress: false,
      };
    default:
      return state;
  }
}
